import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";

// Styles and components from Explore V2 Strategy Page
import {
  PageWrapper,
  ChartCard,
  ContentGrid,
  RightSidebarWrapper,
} from "shared/StrategyOverview/v2/Components/StrategyPageV2.styles";
import BuildStrategy from "shared/StrategyOverview/v2/Components/BuildStrategy";
import News from "features/Research/v2/components/News";
import StrategyTreemap from "features/Explore/v2/Components/StrategyTreemap";
import SimilarStrategies from "shared/StrategyOverview/v2/Components/SimilarStrategies";
import TopHoldings from "shared/StrategyOverview/v2/Components/Tables/TopHoldings";
import YourActiveHoldings from "shared/StrategyOverview/v2/Components/Tables/YourActiveHoldings";
import RecentTransactions from "shared/StrategyOverview/v2/Components/Tables/RecentTransactions";
import HeroCard from "shared/StrategyOverview/v2/Components/HeroCard";
import HeroCardSkeleton from "shared/StrategyOverview/v2/Components/HeroCard/HeroCardSkeleton";
import HeroCardError from "shared/StrategyOverview/v2/Components/HeroCard/HeroCardError";
import ReturnValue from "features/Dashboard/v2/Components/AccountDetails/ReturnValue";
import StrategyChart from "shared/StrategyOverview/v2/Components/StrategyChart";

// Utils and hooks
import { getStockCompanyIcon } from "utils/index";
// import { isFreeUser } from "utils/index";
import { useStrategyDetails } from "features/Strategy/hook/useStrategyDetails";
import { useStrategyOverview } from "features/Strategy/hook/useStrategyOverview";
// import { useStrategyReturns } from "hooks/useStrategyReturns";
import { default as KeyStats } from "features/Explore/v2/Components/KeyStats";
import AvailableBrokersCard from "features/Explore/v2/Components/AvailableBrokersCard";
import InvestmentOrderCard from "features/Explore/v2/Components/InvestmentOrderCard";
// import { getHPerformanceData } from "features/Dashboard/utils";
import { scrollTopValueSelect } from "Layout/slice/selector";
import {
  setBreadcrumbDetailName,
  clearBreadcrumbDetailName,
} from "Layout/slice";
import PublicAccessCTA from "shared/StrategyOverview/v2/Components/PublicAccessCTA";
import { useNavigation } from "utils/navigate";
import { StrategyShareModal } from "shared/Modal/StrategyShareModal";
import {
  filterChartByTimeframe,
  getReturnSummary,
  getStrategyChartByKey,
  normalizeChartData,
} from "utils/charts";
import { TIME_INDICATOR } from "utils/constants";
import { trackEvent } from "utils/tracking";

export const CHART_VALUE_KEY = "Strategy Value";

const StrategyOverviewPageV2 = ({ isPublicStrategy = false }) => {
  const dispatch = useDispatch();
  const scrollTopValue = useSelector(scrollTopValueSelect);
  const { id } = useParams();
  const { navigateToV2Signup, navigateToV2Signin } = useNavigation();
  const [showCta, setShowCta] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    data: strategyDetailData,
    isLoading: isDetailsLoading,
    error: detailsError,
  } = useStrategyDetails(id, isPublicStrategy);

  const {
    data: strategyOverviewData,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = useStrategyOverview(id);

  // const { data: strategyReturnsData, isLoading: isReturnsLoading } =
  //   useStrategyReturns(id);

  // Public or free plan should see blurred content + lock modal
  // const isFreePlan = isFreeUser();
  const isHiddenContent = isPublicStrategy;

  // const hasReturnData = useMemo(() => {
  //   const hasToday = Boolean(
  //     strategyReturnsData?.todays_return?.amount ||
  //       strategyReturnsData?.todays_return?.percentage ||
  //       (Array.isArray(strategyReturnsData?.todays_return_chart) &&
  //         strategyReturnsData?.todays_return_chart?.length > 0)
  //   );
  //   const hasNet = Boolean(
  //     strategyReturnsData?.net_return?.amount ||
  //       strategyReturnsData?.net_return?.percentage ||
  //       (Array.isArray(strategyReturnsData?.net_return_chart) &&
  //         strategyReturnsData?.net_return_chart?.length > 0)
  //   );
  //   return hasToday || hasNet;
  // }, [strategyReturnsData]);

  // Memoize data
  const treemapData = useMemo(() => {
    return strategyDetailData?.graphs?.industries || {};
  }, [strategyDetailData?.graphs?.industries]);

  const heroCardData = useMemo(() => {
    // Helper: safely prefer overview > details > fallback
    const prefer = (key) =>
      strategyOverviewData?.[key] ?? strategyDetailData?.[key] ?? undefined;

    // Extract key stats safely
    const getStatValue = (name) =>
      strategyOverviewData?.key_stats?.find((s) => s.name === name)?.value ??
      strategyDetailData?.key_stats?.find((s) => s.name === name)?.value ??
      "";
    // Combine & normalize data
    return {
      id: prefer("strategy_id"),
      title: prefer("name") || "",
      authorName: prefer("author_name") || "--",
      authorId:
        strategyOverviewData?.author_id ?? strategyDetailData?.author_id,
      description: prefer("description") || "--",
      coverPhoto:
        strategyOverviewData?.cover_photo ||
        strategyDetailData?.cover_photo ||
        strategyOverviewData?.strategy_image ||
        strategyDetailData?.strategy_image ||
        "/src/assets/images/PlanCardBg1.png",

      // Overview-specific fields
      investment:
        strategyOverviewData?.investment?.formatted ||
        strategyDetailData?.investment?.formatted,
      // investors: strategyOverviewData?.investors?.formatted,
      industry:
        strategyOverviewData?.graph?.top_industry ||
        strategyDetailData?.graphs?.top_industry ||
        "--",

      // Top holdings (limit to 4, map to icons)
      topHoldings:
        strategyOverviewData?.graph?.top_holdings
          ?.slice(0, 4)
          ?.map((symbol) => getStockCompanyIcon(symbol)) ??
        strategyDetailData?.graphs?.top_holdings
          ?.slice(0, 4)
          ?.map((symbol) => getStockCompanyIcon(symbol?.name || "")) ??
        [],

      // Key stats (overview > details)
      assetType: getStatValue("Asset Classes"),
      annualReturn: getStatValue("Annual Return"),
      risk: strategyOverviewData?.risk_score ?? strategyDetailData?.risk_score,
    };
  }, [strategyOverviewData, strategyDetailData]);

  useEffect(() => {
    if (id && strategyDetailData?.name) {
      trackEvent({
        name: "view_strategy_details",
        label: "Strategy",
        strategy_id: id,
        strategy_name: strategyDetailData.name,
      });
    }
  }, [id, strategyDetailData?.name]);

  // breadcrumb helper
  useEffect(() => {
    if (heroCardData?.title) {
      dispatch(setBreadcrumbDetailName(heroCardData.title));
    }
    return () => {
      dispatch(clearBreadcrumbDetailName());
    };
  }, [heroCardData?.title]);

  // Toggle CTA visibility when user scrolls past chart and near the bottom
  useEffect(() => {
    if (!isHiddenContent) return;

    const chartElement = document.querySelector(".historical-chart-card");
    if (!chartElement) return;

    const chartRect = chartElement.getBoundingClientRect();
    const stickyHeight = 500;

    const isChartVisible =
      chartRect.top < window.innerHeight - stickyHeight &&
      chartRect.bottom > stickyHeight;

    const scrolledToBottom =
      window.innerHeight + scrollTopValue >
      document.documentElement.offsetHeight;

    const shouldShowCTA = !isChartVisible && scrolledToBottom;
    setShowCta(shouldShowCTA);
  }, [scrollTopValue, isHiddenContent]);

  const lastMonthSummary = useMemo(() => {
    const PERIOD = "1M";
    const threeMonthData = getStrategyChartByKey(
      strategyDetailData?.overview_chart || [],
      "3M" // from the backend as its not having key of 1M
    );
    const lastMonthData = filterChartByTimeframe(threeMonthData, PERIOD);
    const normalized = normalizeChartData(lastMonthData, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  const allTimeSummary = useMemo(() => {
    const PERIOD = "MAX";
    const filtered = filterChartByTimeframe(
      getStrategyChartByKey(strategyDetailData?.overview_chart || [], PERIOD),
      PERIOD
    );
    const normalized = normalizeChartData(filtered, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  return (
    <Row>
      <Col md={12} lg={9}>
        <PageWrapper>
          {isHiddenContent && (
            <Helmet>
              <meta name="robots" content="noindex, nofollow"></meta>
            </Helmet>
          )}
          {isOverviewLoading ? (
            <HeroCardSkeleton />
          ) : overviewError ? (
            <HeroCardError
              error={overviewError}
              onRetry={() => window.location.reload()}
            />
          ) : (
            <HeroCard
              data={heroCardData}
              onShareClick={() => {
                trackEvent({
                  name: "strategy_share",
                  label: "Strategy",
                  strategy_id: id,
                  strategy_name: strategyDetailData?.name,
                });
                setShowShareModal(true);
              }}
            />
          )}

          <div className="historical-chart-card">
            <ChartCard>
              <StrategyChart strategyId={id} isPublic={isPublicStrategy} />
            </ChartCard>
          </div>
          <Row>
            <Col md={6}>
              <ReturnValue title="Last month's return" {...lastMonthSummary} />
            </Col>
            <Col md={6}>
              <ReturnValue title="Total return" {...allTimeSummary} />
            </Col>
          </Row>

          <ContentGrid
            id="blur-content"
            className={`${isHiddenContent ? "blur-overlay" : ""}`}
          >
            <KeyStats
              stats={strategyDetailData?.key_stats || []}
              isLoading={isDetailsLoading}
              error={detailsError}
            />
            <YourActiveHoldings />
            {strategyDetailData?.graphs?.top_holdings?.length > 0 && (
              <TopHoldings
                data={strategyDetailData?.graphs?.top_holdings || []}
                isLoading={isDetailsLoading}
                error={detailsError}
                strategyId={id}
              />
            )}
            {treemapData && (
              <StrategyTreemap
                data={treemapData}
                isLoading={isDetailsLoading}
                error={detailsError}
              />
            )}
            <RecentTransactions strategyId={id} />
            <News strategyId={id} />
            <SimilarStrategies strategyId={id} />
            <BuildStrategy />
          </ContentGrid>
          <StrategyShareModal
            showShareModal={showShareModal}
            toggleShareModal={() => setShowShareModal(false)}
            strategyDetail={strategyOverviewData || strategyDetailData || {}}
            coverPhoto={heroCardData?.coverPhoto}
          />
          {isHiddenContent && (
            <PublicAccessCTA
              visible={showCta}
              onSignup={() => navigateToV2Signup()}
              onSignin={() => navigateToV2Signin()}
            />
          )}
        </PageWrapper>
      </Col>

      <Col md={12} lg={3}>
        <RightSidebarWrapper>
          <PageWrapper>
            <InvestmentOrderCard isPublicStrategy={isPublicStrategy} />
            {strategyDetailData?.supported_brokers?.length > 0 && (
              <AvailableBrokersCard
                brokers={strategyDetailData?.supported_brokers || []}
                error={detailsError}
              />
            )}
          </PageWrapper>
        </RightSidebarWrapper>
      </Col>
    </Row>
  );
};

StrategyOverviewPageV2.propTypes = {
  isPublicStrategy: PropTypes.bool,
};

export default StrategyOverviewPageV2;
